// FIX: Import missing React hooks and types.
import React, { useState, useRef, useEffect, ChangeEvent, useMemo, FormEvent } from 'react';
import PageHeader from '../PageHeader';
import { SavablePageProps, Signature, NewSignature } from '../../types';
import { API_URL, API_KEY_EN } from '../../user_constant.tsx';
import CryptoJS from 'crypto-js';

interface AcceptancePageProps extends SavablePageProps {
  onAccept: () => void;
}

// Signature Pad Component
const SignaturePad: React.FC<{ 
    label: string; 
    signature: string | null;
    onSign: (dataUrl: string | null) => void;
    highlight?: boolean;
    isPrintView?: boolean;
    readOnly?: boolean;
}> = ({ label, signature, onSign, highlight = false, isPrintView = false, readOnly = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    if (isPrintView) {
        return (
           <div className="print:mb-1">
               <label className="block text-sm font-semibold text-slate-700 mb-2 print:mb-1 print:text-xs print:text-slate-500">{label}</label>
               <div className="relative w-full h-32 bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-center print:h-24 print:bg-transparent print:rounded-none">
                   {signature ? (
                       <img src={signature} alt={label} className="max-w-full max-h-full object-contain" />
                   ) : (
                       <span className="text-sm text-slate-500 font-medium">
                           [ Not Signed ]
                       </span>
                   )}
               </div>
           </div>
       );
   }

    const getCoords = (event: MouseEvent | TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        if (event instanceof MouseEvent) return { x: event.clientX - rect.left, y: event.clientY - rect.top };
        if (event.touches?.[0]) return { x: event.touches[0].clientX - rect.left, y: event.touches[0].clientY - rect.top };
        return { x: 0, y: 0 };
    };

    const startDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly) return;
        event.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        const { x, y } = getCoords(event.nativeEvent);
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly || !isDrawing) return;
        event.preventDefault();
        const { x, y } = getCoords(event.nativeEvent);
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (readOnly || !isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) onSign(canvas.toDataURL('image/jpeg'));
    };
    
    const clearCanvas = () => {
        if (readOnly) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx && canvas) {
            const rect = canvas.getBoundingClientRect();
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, rect.width, rect.height);
            onSign(null);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, rect.width, rect.height);

        if (signature) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, rect.width, rect.height);
                ctx.drawImage(img, 0, 0, rect.width, rect.height);
            }
            img.src = signature;
        }
    }, [signature]);

    return (
        <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div 
                className={`relative w-full h-48 bg-white border-2 border-dashed border-slate-300 rounded-lg transition-colors duration-200 ${highlight ? 'animate-pulse-highlight' : ''} ${readOnly ? 'cursor-not-allowed bg-slate-50' : 'cursor-crosshair hover:border-brand-primary/80'}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <canvas
                    ref={canvasRef}
                    className="w-full h-full rounded-lg"
                    onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                />
                {!signature && !readOnly && <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">Sign here</span>}
                {readOnly && signature && (
                    <div className="absolute top-2 right-2 flex items-center bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full z-10 pointer-events-none">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Signed
                    </div>
                )}
                {!readOnly && signature && isHovered && (
                     <button type="button" onClick={clearCanvas} className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors z-10" aria-label="Clear signature">
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.22-2.365.468a.75.75 0 10.23 1.482l.149-.046a.75.75 0 01.701.043l2.25 1.5a.75.75 0 01.043.701l-.046.149a.75.75 0 101.482.23l.468-2.365v-.443A2.75 2.75 0 008.75 1zM3.75 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 013.75 8zM8 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 018 8zM12.25 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5a.75.75 0 01.75-.75zM16 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 0116 8z" clipRule="evenodd" /></svg>
                    </button>
                )}
            </div>
        </div>
    );
};

// Form Field Component
const FormField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    value?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    readOnly?: boolean;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, placeholder = '', value, onChange, readOnly = false, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
            <div className="print:mb-1">
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">
                    {value || '-'}
                </div>
            </div>
        );
    }
    
    const baseClasses = "block w-full px-4 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200";
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed"
        : "bg-white text-slate-900 placeholder-slate-400 hover:border-slate-400";
    
    return (
        <div>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <input
                id={name}
                name={name}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                readOnly={readOnly}
                className={`${baseClasses} ${statefulClasses} ${highlight ? 'animate-pulse-highlight' : ''}`}
            />
        </div>
    );
};

// New Date Field Component with "Now" button
const DateField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    value?: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onSetNow: () => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, placeholder, value, onChange, onSetNow, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
            <div className="print:mb-1">
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">
                    {value || '-'}
                </div>
            </div>
        );
    }
    
    return (
    <div>
        <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <div className="relative">
            <input
                id={name}
                name={name}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                className={`block w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary hover:border-slate-400 transition-all duration-200 ${highlight ? 'animate-pulse-highlight' : ''}`}
            />
            <button
                type="button"
                onClick={onSetNow}
                className="absolute top-1/2 right-2 -translate-y-1/2 px-3 py-1 text-xs font-semibold text-brand-primary bg-brand-primary-light rounded-md hover:bg-brand-primary/20 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            >
                Now
            </button>
        </div>
    </div>
    )
};


// Declaration Text Component
const DeclarationText: React.FC<{
    name: string;
    nric: string;
    address: string;
}> = ({ name, nric, address }) => {
    const valueStyle = "font-semibold text-slate-800 underline decoration-slate-300 decoration-dotted underline-offset-4 mx-1 px-1";
    return (
        <p className="text-sm text-slate-600 leading-relaxed">
            I, <span className={valueStyle}>{name || '[Name]'}</span>, NRIC No. <span className={valueStyle}>{nric || '[IC / Passport]'}</span> of <span className={valueStyle}>{address || '[Address]'}</span> hereby acknowledge and confirm that I/We read and understood all the terms and conditions hereinabove. I/We hereby agree and accept the terms and conditions hereinabove.
        </p>
    );
};

// Main Section Wrapper
const SignatorySection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-slate-50/70 p-6 rounded-xl border border-slate-200 mb-8 print:bg-transparent print:border-none print:p-0 print:mb-4 print:break-inside-avoid">
        <h3 className="text-xl font-semibold text-slate-800 mb-6 border-b border-slate-200 pb-4 print:text-lg print:mb-3 print:pb-2">{title}</h3>
        {children}
    </div>
);

const isFieldEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && isNaN(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error('Invalid data URL');
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

const AcceptancePage: React.FC<AcceptancePageProps> = ({ onAccept, data, onUpdate, isSaving, isClientView, isPrintView = false }) => {
  const findSignature = (type: string) => data.signatures.find(s => s.type === type);
  const formatDate = (isoString: string | null | undefined) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
          return '';
      }
      return date.toISOString().split('T')[0].split('-').reverse().join('/');
  };
  const formatDateForPayload = (displayDate: string) => {
    if (!displayDate) return null;
    const parts = displayDate.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!parts) return null;
    return new Date(`${parts[3]}-${parts[2]}-${parts[1]}`).toISOString();
  }

  // Local state for new base64 signatures
  const [residentSignature, setResidentSignature] = useState<string | null>(null);
  const [residentWitnessSignature, setResidentWitnessSignature] = useState<string | null>(null);
  const [suretySignature, setSuretySignature] = useState<string | null>(null);
  const [suretyWitnessSignature, setSuretyWitnessSignature] = useState<string | null>(null);

  // Local state for displaying fetched signature URLs
  const [displayResidentSignature, setDisplayResidentSignature] = useState<string | null>(null);
  const [displayResidentWitnessSignature, setDisplayResidentWitnessSignature] = useState<string | null>(null);
  const [displaySuretySignature, setDisplaySuretySignature] = useState<string | null>(null);
  const [displaySuretyWitnessSignature, setDisplaySuretyWitnessSignature] = useState<string | null>(null);

  // Form fields state
  const [residentFullName, setResidentFullName] = useState('');
  const [residentIcPassport, setResidentIcPassport] = useState('');
  const [residentDate, setResidentDate] = useState('');
  const [residentWitnessFullName, setResidentWitnessFullName] = useState('');
  const [residentWitnessId, setResidentWitnessId] = useState('');
  const [residentWitnessDate, setResidentWitnessDate] = useState('');
  
  const [suretyFullName, setSuretyFullName] = useState('');
  const [suretyIcPassport, setSuretyIcPassport] = useState('');
  const [suretyDate, setSuretyDate] = useState('');
  const [suretyWitnessFullName, setSuretyWitnessFullName] = useState('');
  const [suretyWitnessId, setSuretyWitnessId] = useState('');
  const [suretyWitnessDate, setSuretyWitnessDate] = useState('');
  
  const [isUploadingSignatures, setIsUploadingSignatures] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialState, setInitialState] = useState(data);

  const isResidentSignReady = !isFieldEmpty(residentDate);
  const isResidentWitnessSignReady = !isFieldEmpty(residentWitnessFullName) && !isFieldEmpty(residentWitnessId) && !isFieldEmpty(residentWitnessDate);
  const isSuretySignReady = !isFieldEmpty(suretyDate);
  const isSuretyWitnessSignReady = !isFieldEmpty(suretyWitnessFullName) && !isFieldEmpty(suretyWitnessId) && !isFieldEmpty(suretyWitnessDate);

   const decryptedCustomerId = useMemo(() => {
        if (!data.customer_id) return '';
        try {
            const encKey = "CareConcierge";
            const decrypted = CryptoJS.AES.decrypt(data.customer_id, encKey);
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error("Failed to decrypt customer ID:", error);
            return 'Decryption Error';
        }
    }, [data.customer_id]);

  useEffect(() => {
    const fetchSignatureUrl = async (s3Url: string) => {
            const apiUrl = `${API_URL}/resident/attachment?url=${encodeURIComponent(s3Url)}`;
            try {
                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: { 'x-api-key': API_KEY_EN },
                });
                if (!response.ok) return null;
                const responseText = await response.text();
                let url: string | null = null;
                try {
                    const parsed = JSON.parse(responseText);
                    url = typeof parsed === 'string' ? parsed : parsed.url || parsed.downloadUrl;
                } catch {
                    url = responseText.startsWith('http') ? responseText : null;
                }
                return url;
            } catch {
                return null;
            }
        };

        const signaturesMap = {
            cr_acceptance_type: setDisplayResidentSignature,
            cr_witness_type: setDisplayResidentWitnessSignature,
            co_acceptance_type: setDisplaySuretySignature,
            co_witness_type: setDisplaySuretyWitnessSignature,
        };

        data.signatures.forEach(sig => {
            if (sig.type in signaturesMap && sig.signature_url_s3) {
                fetchSignatureUrl(sig.signature_url_s3).then(url => {
                    if(url) signaturesMap[sig.type as keyof typeof signaturesMap](url);
                });
            }
        });
  }, [data.signatures]);

  useEffect(() => {
    setInitialState(data);
    const residentSignData = findSignature('cr_acceptance_type');
    const residentWitnessSignData = findSignature('cr_witness_type');
    const suretySignData = findSignature('co_acceptance_type');
    const suretyWitnessSignData = findSignature('co_witness_type');

    setResidentFullName(residentSignData?.name || data.cr_name || '');
    setResidentIcPassport(residentSignData?.ic_passport || data.cr_ic_passport || '');
    setResidentDate(formatDate(residentSignData?.date));
    setResidentWitnessFullName(residentWitnessSignData?.name || '');
    setResidentWitnessId(residentWitnessSignData?.ic_passport || '');
    setResidentWitnessDate(formatDate(residentWitnessSignData?.date));

    setSuretyFullName(suretySignData?.name || data.co_name || '');
    setSuretyIcPassport(suretySignData?.ic_passport || data.co_ic_passport || '');
    setSuretyDate(formatDate(suretySignData?.date));
    setSuretyWitnessFullName(suretyWitnessSignData?.name || '');
    setSuretyWitnessId(suretyWitnessSignData?.ic_passport || '');
    setSuretyWitnessDate(formatDate(suretyWitnessSignData?.date));
  }, [data]);

  const isDirty = useMemo(() => {
    return residentSignature !== null || residentWitnessSignature !== null || suretySignature !== null || suretyWitnessSignature !== null;
  }, [residentSignature, residentWitnessSignature, suretySignature, suretyWitnessSignature]);


  const isFormAccepted = (residentSignature || displayResidentSignature) && (suretySignature || displaySuretySignature);

  const handleSetNow = (setter: React.Dispatch<React.SetStateAction<string>>) => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      setter(`${day}/${month}/${year}`);
  };

    const uploadSignature = async (dataUrl: string, type: string): Promise<string | null> => {
        const LOG_PREFIX = `[Signature Upload - Acceptance]`;
        console.log(`${LOG_PREFIX} Starting for type: ${type}`);
    
        if (!decryptedCustomerId || decryptedCustomerId === 'Decryption Error') {
            console.error(`${LOG_PREFIX} FAILED: Cannot upload signature without a valid customer ID.`);
            return null;
        }
        console.log(`${LOG_PREFIX} Customer ID found: ${decryptedCustomerId}`);
    
        try {
            const filename = `${type}.jpg`;
            const prefix = `${decryptedCustomerId}/${type}/signatures`;
            
            const presignedUrlPayload = {
                payload: [{ prefix, filename }],
            };
            console.log(`${LOG_PREFIX} Requesting pre-signed URL with payload:`, JSON.stringify(presignedUrlPayload, null, 2));
    
            const presignedUrlApi = `${API_URL}/resident/attachment`;
            const presignedUrlResponse = await fetch(presignedUrlApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY_EN },
                body: JSON.stringify(presignedUrlPayload),
            });
            
            if (!presignedUrlResponse.ok) {
                const errorText = await presignedUrlResponse.text();
                console.error(`${LOG_PREFIX} FAILED: Pre-signed URL request failed with status ${presignedUrlResponse.status}. Response: ${errorText}`);
                throw new Error('Failed to get pre-signed URL for signature.');
            }
            
            const responseData = await presignedUrlResponse.json();
            console.log(`${LOG_PREFIX} Received pre-signed URL response:`, JSON.stringify(responseData, null, 2));

            const filePath = `${prefix}/${filename}`;
            const responseArray = responseData[filePath];
            const uploadInfo = Array.isArray(responseArray) && responseArray.length > 0 ? responseArray[0] : null;

            if (!uploadInfo || !uploadInfo.uploadUrl || !uploadInfo.downloadUrl) {
                console.error(`${LOG_PREFIX} FAILED: Invalid pre-signed URL response. 'uploadUrl' or 'downloadUrl' missing.`);
                throw new Error('Invalid pre-signed URL response for signature.');
            }
            console.log(`${LOG_PREFIX} Pre-signed URL received. Uploading to: ${uploadInfo.uploadUrl}`);
    
            const file = dataURLtoFile(dataUrl, filename);
            console.log(`${LOG_PREFIX} Converted data URL to file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
            
            const uploadResponse = await fetch(uploadInfo.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error(`${LOG_PREFIX} FAILED: S3 upload failed with status ${uploadResponse.status}. Response: ${errorText}`);
                throw new Error('Signature upload to S3 failed.');
            }
    
            console.log(`${LOG_PREFIX} SUCCESS: Upload successful for type: ${type}. Download URL: ${uploadInfo.downloadUrl}`);
            return uploadInfo.downloadUrl;
        } catch (error) {
            console.error(`${LOG_PREFIX} FAILED: An error occurred during the process for type ${type}:`, error);
            alert(`Failed to save signature. Please try again.`);
            return null;
        }
    };

  const performSave = async () => {
    setIsUploadingSignatures(true);
    const newSignatures: NewSignature[] = [];
    
    const signaturesToUpload = [
        { type: 'cr_acceptance_type', data: residentSignature, name: residentFullName, ic: residentIcPassport, date: formatDateForPayload(residentDate) },
        { type: 'cr_witness_type', data: residentWitnessSignature, name: residentWitnessFullName, ic: residentWitnessId, date: formatDateForPayload(residentWitnessDate) },
        { type: 'co_acceptance_type', data: suretySignature, name: suretyFullName, ic: suretyIcPassport, date: formatDateForPayload(suretyDate) },
        { type: 'co_witness_type', data: suretyWitnessSignature, name: suretyWitnessFullName, ic: suretyWitnessId, date: formatDateForPayload(suretyWitnessDate) },
    ];
    
    const uploadPromises = signaturesToUpload.map(async (sig) => {
        if (sig.data && sig.data.startsWith('data:image')) {
            const url = await uploadSignature(sig.data, sig.type);
            if (url) {
                newSignatures.push({
                    type: sig.type,
                    signature_url_s3: url,
                    name: sig.name,
                    ic_passport: sig.ic,
                    date: sig.date || new Date().toISOString(),
                });
            }
        }
    });

    await Promise.all(uploadPromises);
    setIsUploadingSignatures(false);

    try {
        if(newSignatures.length > 0) {
            await onUpdate({ signatures: newSignatures });
        }
        setResidentSignature(null);
        setResidentWitnessSignature(null);
        setSuretySignature(null);
        setSuretyWitnessSignature(null);
    } catch (error) {
        console.error("Failed to save acceptance signatures:", error);
        throw error;
    }
  };

  const handleSaveSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
        await performSave();
    } catch (error) {}
  };

  const handleNextClick = async () => {
    try {
        if (isDirty) {
            await performSave();
        }
        onAccept();
    } catch (error) {
        console.log("Save failed, navigation cancelled.");
    }
  };

  const SaveButton = (
    <button type="submit" disabled={isSaving || isUploadingSignatures || !isDirty} className="w-full sm:w-auto px-8 py-3 bg-slate-600 text-white font-bold rounded-lg shadow-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
        {isSaving || isUploadingSignatures ? 'Saving...' : 'Save'}
    </button>
  );

  const NextButton = (
    <button
      type="button"
      onClick={handleNextClick}
      disabled={!isFormAccepted || isSaving || isUploadingSignatures}
      className="w-full sm:w-auto px-8 py-3 font-bold rounded-lg shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed bg-brand-primary text-white hover:bg-brand-primary/90 flex items-center justify-center"
    >
      Next
    </button>
  );

  const actionButtons = <div className="flex items-center gap-4">{SaveButton}{NextButton}</div>;
  
  const commonPrintProps = { isPrintView };

  const formContent = (
    <>
      <SignatorySection title="Signed by/for and on behalf of the Resident">
          <DeclarationText name={data.cr_name || ''} nric={data.cr_ic_passport || ''} address={data.cr_address || ''} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mt-6 print:gap-y-4">
              <div className="space-y-6 print:space-y-2">
                  <h4 className="text-base font-semibold text-slate-600">Signed By:</h4>
                  <SignaturePad {...commonPrintProps} readOnly={!!displayResidentSignature || !isResidentSignReady} label="Signature" signature={residentSignature || displayResidentSignature} onSign={setResidentSignature} highlight={isClientView && isFieldEmpty(residentSignature || displayResidentSignature)} />
                  {!isResidentSignReady && !displayResidentSignature && !isPrintView && (
                      <p className="text-xs text-red-500 -mt-4 px-1">
                          Please fill in the Date before signing.
                      </p>
                  )}
                  <FormField {...commonPrintProps} label="Full Name" name="residentFullName" value={residentFullName} readOnly />
                  <FormField {...commonPrintProps} label="IC / Passport" name="residentIcPassport" value={residentIcPassport} readOnly />
                  <DateField {...commonPrintProps} label="Date" name="residentDate" placeholder="DD/MM/YYYY" value={residentDate} onChange={e => setResidentDate(e.target.value)} onSetNow={() => handleSetNow(setResidentDate)} highlight={isClientView && isFieldEmpty(residentDate)} />
              </div>
              <div className="space-y-6 print:space-y-2">
                  <h4 className="text-base font-semibold text-slate-600">Witnessed By:</h4>
                  <SignaturePad {...commonPrintProps} readOnly={isClientView || !!displayResidentWitnessSignature || !isResidentWitnessSignReady} label="Signature" signature={residentWitnessSignature || displayResidentWitnessSignature} onSign={setResidentWitnessSignature} highlight={!isClientView && isFieldEmpty(residentWitnessSignature || displayResidentWitnessSignature)} />
                  {!isResidentWitnessSignReady && !displayResidentWitnessSignature && !isClientView && !isPrintView && (
                      <p className="text-xs text-red-500 -mt-4 px-1">
                          Please fill in Full Name, ID, and Date before signing.
                      </p>
                  )}
                  <FormField {...commonPrintProps} label="Full Name" name="residentWitnessFullName" value={residentWitnessFullName} onChange={e => setResidentWitnessFullName(e.target.value)} highlight={!isClientView && isFieldEmpty(residentWitnessFullName)} />
                  <FormField {...commonPrintProps} label="IC / Passport / Staff ID" name="residentWitnessId" value={residentWitnessId} onChange={e => setResidentWitnessId(e.target.value)} highlight={!isClientView && isFieldEmpty(residentWitnessId)} />
                  <DateField {...commonPrintProps} label="Date" name="residentWitnessDate" placeholder="DD/MM/YYYY" value={residentWitnessDate} onChange={e => setResidentWitnessDate(e.target.value)} onSetNow={() => handleSetNow(setResidentWitnessDate)} highlight={!isClientView && isFieldEmpty(residentWitnessDate)} />
              </div>
          </div>
      </SignatorySection>

      <SignatorySection title="Signed by/for and on behalf of the Surety">
          <DeclarationText name={data.co_name || ''} nric={data.co_ic_passport || ''} address={data.co_address || ''} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mt-6 print:gap-y-4">
              <div className="space-y-6 print:space-y-2">
                  <h4 className="text-base font-semibold text-slate-600">Signed By:</h4>
                  <SignaturePad {...commonPrintProps} readOnly={!!displaySuretySignature || !isSuretySignReady} label="Signature" signature={suretySignature || displaySuretySignature} onSign={setSuretySignature} highlight={isClientView && isFieldEmpty(suretySignature || displaySuretySignature)} />
                  {!isSuretySignReady && !displaySuretySignature && !isPrintView && (
                        <p className="text-xs text-red-500 -mt-4 px-1">
                            Please fill in the Date before signing.
                        </p>
                  )}
                  <FormField {...commonPrintProps} label="Full Name" name="suretyFullName" value={suretyFullName} readOnly />
                  <FormField {...commonPrintProps} label="IC / Passport" name="suretyIcPassport" value={suretyIcPassport} readOnly />
                  <DateField {...commonPrintProps} label="Date" name="suretyDate" placeholder="DD/MM/YYYY" value={suretyDate} onChange={e => setSuretyDate(e.target.value)} onSetNow={() => handleSetNow(setSuretyDate)} highlight={isClientView && isFieldEmpty(suretyDate)} />
              </div>
              <div className="space-y-6 print:space-y-2">
                  <h4 className="text-base font-semibold text-slate-600">Witnessed By:</h4>
                  <SignaturePad {...commonPrintProps} readOnly={isClientView || !!displaySuretyWitnessSignature || !isSuretyWitnessSignReady} label="Signature" signature={suretyWitnessSignature || displaySuretyWitnessSignature} onSign={setSuretyWitnessSignature} highlight={!isClientView && isFieldEmpty(suretyWitnessSignature || displaySuretyWitnessSignature)} />
                  {!isSuretyWitnessSignReady && !displaySuretyWitnessSignature && !isClientView && !isPrintView && (
                        <p className="text-xs text-red-500 -mt-4 px-1">
                            Please fill in Full Name, ID, and Date before signing.
                        </p>
                  )}
                  <FormField {...commonPrintProps} label="Full Name" name="suretyWitnessFullName" value={suretyWitnessFullName} onChange={e => setSuretyWitnessFullName(e.target.value)} highlight={!isClientView && isFieldEmpty(suretyWitnessFullName)} />
                  <FormField {...commonPrintProps} label="IC / Passport / Staff ID" name="suretyWitnessId" value={suretyWitnessId} onChange={e => setSuretyWitnessId(e.target.value)} highlight={!isClientView && isFieldEmpty(suretyWitnessId)} />
                  <DateField {...commonPrintProps} label="Date" name="suretyWitnessDate" placeholder="DD/MM/YYYY" value={suretyWitnessDate} onChange={e => setSuretyWitnessDate(e.target.value)} onSetNow={() => handleSetNow(setSuretyWitnessDate)} highlight={!isClientView && isFieldEmpty(suretyWitnessDate)} />
              </div>
          </div>
      </SignatorySection>
    </>
  );

  if (isPrintView) {
    return <div className="animate-fade-in">{formContent}</div>;
  }

  return (
    <div className="animate-fade-in">
      <form className="space-y-4" onSubmit={handleSaveSubmit}>
          <PageHeader
            title="Service Agreement - Acceptance"
            subtitle="Please review and sign to accept the terms of the service agreement."
            action={actionButtons}
            isPrintView={isPrintView}
          />
          {formContent}
          <div className="pt-6 flex justify-end">
            {actionButtons}
          </div>
      </form>
    </div>
  );
};

export default AcceptancePage;